<template>
  <div>
    <h1>Request payment on file</h1>
    <FileWorking :file="file" />
    <v-card>
      <v-card-text>
        <h2>Request payment on file</h2>
        <v-alert :value="true" type="info"
          >Enter the amount below that you wish to request. Add a note if
          required.</v-alert
        >
        <v-progress-circular indeterminate color="info" v-if="!showForm" />
        <v-form ref="requestForm" v-if="showForm">
          <v-text-field
            label="Category"
            v-model="category.shortname"
            readonly
          />
          <v-text-field
            type="number"
            label="Amount"
            v-model="request.amount"
            outline
            :rules="valueRules"
          />
          <v-select
            v-model="request.purposeid"
            :items="purposes"
            item-value="id"
            item-text="body"
            label="Purpose"
            outline
            :rules="valueRules"
          />
          <v-textarea
            outline
            label="Note (optional)"
            v-model="request.note"
          ></v-textarea>
          <v-btn @click="addRequest" color="info">
            <v-icon>save</v-icon>Request payment
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
    <v-card>
      <v-card-text>
        <h2>Previous payment requests</h2>
        <p>
          Listed below are any payments that have been previously requested on
          this file
        </p>
        <table border="1">
          <tr>
            <th v-if="$keycloak.hasRealmRole('root')">34</th>
            <th>Id</th>
            <th>Requested</th>
            <th>Reference</th>
            <th>Category</th>
            <th>Amount</th>
            <th>Purpose</th>
            <th>Status</th>
          </tr>
          <tr v-for="request in modifiedRequests" :key="request.id">
            <td v-if="$keycloak.hasRealmRole('root')">
              <router-link
                :to="{ name: 'request', params: { id: request.id } }"
                v-if="request.status == 0"
                >Authorise</router-link
              >
              <router-link
                :to="{ name: 'process', params: { id: request.id } }"
                v-if="request.status == 1"
                >Process</router-link
              >
            </td>
            <td>{{ request.id }}</td>
            <td>{{ request.createdHuman }}</td>
            <td>
              <span v-if="request.file">
                <FileZ :file="request.file" />
              </span>
              <span v-if="!request.file">{{ request.fileid }}</span>
            </td>
            <td>
              <span v-if="request.category">{{
                request.category.shortname
              }}</span>
              <!-- <span v-if="!request.category">{{request.fileid}}</span></td> -->
            </td>
            <td>{{ request.amount | toCurrency }}</td>
            <td>
              <span v-if="request.purpose">{{ request.purpose.body }}</span>
              <span v-if="!request.purpose">{{ request.purposeid }}</span>
            </td>
            <td>
              <span class="status0" v-if="request.status == 0"
                >Awaiting authorisation</span
              >
              <span class="status1" v-if="request.status == 1"
                >Payment authorised. Awaiting processing.</span
              >
              <span class="status2" v-if="request.status == 2">Success</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="download(request)"
                    v-if="request.status == 2"
                    color="warning"
                    v-on="on"
                    small
                  >
                    <v-icon>picture_as_pdf</v-icon>
                  </v-btn>
                </template>
                <span>Receipt {{ request.id }}</span>
              </v-tooltip>
            </td>
          </tr>
        </table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import nbmlaw from "@/api/nbmlaw";
import FileWorking from "@/components/link/FileWorking";
import FileZ from "@/components/link/FileZ";
import { formatDuration, intervalToDuration, parseISO } from "date-fns";
import { convertListOfObjectsToMap } from "@/util/helpers";
export default {
  name: "PaymentFile",
  metaInfo() {
    return {
      title: "Request payment",
      titleTemplate: "%s - Payments - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true,
      },
    };
  },
  components: { FileWorking, FileZ },
  computed: {
    showForm() {
      return this.file && this.category && this.purposes;
    },
    modifiedRequests() {
      if (!this.requests) {
        return undefined;
      }
      const purposesMap = this.purposes
        ? convertListOfObjectsToMap(this.purposes, "id")
        : undefined;
      return this.requests.map((r) => {
        const purpose =
          purposesMap && purposesMap[r.purposeid]
            ? purposesMap[r.purposeid]
            : undefined;
        const file = this.file ? this.file : undefined;
        const category = this.category ? this.category : undefined;
        const createdHuman = formatDuration(
          intervalToDuration({
            start: new Date(),
            end: parseISO(r.created),
          })
        );
        return {
          ...r,
          purpose,
          file,
          category,
          createdHuman,
        };
      });
    },
  },
  data() {
    return {
      file: null,
      category: null,
      requests: null,
      purposes: null,
      request: {
        amount: 0,
        note: null,
        purposeid: null,
      },
      valueRules: [(v) => !!v || "Value is required"],
    };
  },
  methods: {
    fetchFile() {
      nbmlaw
        .get(`/cors/admin/file/files/${this.$route.params.id}`)
        .then((response) => {
          // console.log(response.data);
          this.file = response.data;
        });
    },
    fetchCategory() {
      nbmlaw
        .get(`/cors/admin/file/files/${this.$route.params.id}/category`)
        .then((response) => {
          // console.log(response.data);
          this.category = response.data;
        });
    },
    fetchRequests() {
      nbmlaw
        .get(`/cors/admin/file/files/${this.$route.params.id}/paymentRequests`)
        .then((response) => {
          // console.log(response.data);
          this.requests = response.data;
        });
    },
    fetchPurposes() {
      nbmlaw.get(`/cors/admin/payment/purposes`).then((response) => {
        // console.log(response.data);
        this.purposes = response.data;
      });
    },
    download(request) {
      window.open(
        `${process.env.VUE_APP_CT_URL}/cors/admin/payment/requests/${request.id}/receipt`,
        "_blank"
      );
    },
    addRequest() {
      if (this.$refs.requestForm.validate()) {
        nbmlaw
          .post(
            `/cors/admin/file/files/${this.$route.params.id}/paymentRequests`,
            this.request
          )
          .then(() => {
            // console.log(response.data);
            this.$router.push({
              name: "file",
              params: { id: this.$route.params.id },
            });
          });
      }
    },
  },
  created() {
    this.fetchPurposes();
    this.fetchFile();
    this.fetchCategory();
    this.fetchRequests();
  },
};
</script>

<style scoped>
.status0 {
  color: red;
}

.status1 {
  color: orange;
}

.status2 {
  color: green;
}
</style>
